import React from "react";
import { Box, Paper, Typography, Skeleton, Chip } from "@mui/material";
import { Timer24Regular, History24Regular } from "@fluentui/react-icons";
import { THEME, useOverviewData, getTimeRangeLabel } from "./OverviewContext";
import { CTLExploreDataButton } from "../utils/CTLExploreDataButton";
import { generateSLAPerformanceQuery } from "../utils/ctlKustoQueries";
import { CTLInfoTooltip } from "../utils/CTLInfoTooltip";

const SLAPerformance: React.FC = () => {
    const { data, isLoading, dateRange, customDateRange } = useOverviewData();

    const timeRangeLabel = getTimeRangeLabel(dateRange, customDateRange);
    const slaStages = data?.slaPerformance ?? [];

    if (isLoading) {
        return <Skeleton variant="rectangular" height={200} sx={{ borderRadius: "12px" }} />;
    }

    if (slaStages.length === 0) {
        return (
            <Paper
                elevation={0}
                sx={{
                    p: 2.5,
                    borderRadius: "12px",
                    border: `1px solid ${THEME.border}`,
                    backgroundColor: THEME.surface
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <Timer24Regular style={{ color: THEME.primary }} />
                    <Typography sx={{ fontSize: "16px", fontWeight: 700, color: THEME.textPrimary }}>
                        Customer Request SLA Tracker
                    </Typography>
                </Box>
                <Typography sx={{ fontSize: "13px", color: THEME.textSecondary, textAlign: "center", py: 4 }}>
                    No SLA data available.
                </Typography>
            </Paper>
        );
    }

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                borderRadius: "12px",
                border: `1px solid ${THEME.border}`,
                backgroundColor: THEME.surface
            }}
        >
            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3, flexWrap: "wrap" }}>
                <Timer24Regular style={{ color: THEME.primary }} />
                <Typography sx={{ fontSize: "16px", fontWeight: 700, color: THEME.textPrimary }}>
                    Customer Request SLA Tracker
                </Typography>
                <CTLInfoTooltip
                    title="Customer Request SLA Tracker"
                    description="Whether we are keeping the promises we make to customers. For each type of outcome it compares how long we actually take against the commitment we have agreed."
                    howItsCalculated="For every decision outcome we average the total number of days from the request being raised to it reaching that outcome, then compare it with the agreed target for that outcome. Green means we are at or inside the target; red means we are running over."
                    note="Each outcome carries its own agreed target, which is why the targets differ from card to card."
                />
                <Chip
                    icon={<History24Regular style={{ fontSize: 12 }} />}
                    label={timeRangeLabel}
                    size="small"
                    sx={{
                        height: 20,
                        fontSize: "10px",
                        fontWeight: 600,
                        backgroundColor: dateRange === "2years" ? "#E7F1FF" : "#FFF4CE",
                        color: dateRange === "2years" ? THEME.primary : "#996800",
                        "& .MuiChip-icon": {
                            color: "inherit",
                            marginLeft: "4px"
                        }
                    }}
                />
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
                    <Typography sx={{ fontSize: "12px", color: THEME.textSecondary }}>
                        Avg End-to-End Days per Decision Status
                    </Typography>
                </Box>
            </Box>

            {/* Status Cards Grid */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, 1fr)",
                        md: `repeat(${Math.min(slaStages.length, 4)}, 1fr)`
                    },
                    gap: 2
                }}
            >
                {slaStages.map((stage) => {
                    const isWithinTarget = stage.avgDays <= stage.targetDays;
                    const bgColor = isWithinTarget ? THEME.successBg : THEME.errorBg;
                    const textColor = isWithinTarget ? THEME.success : THEME.error;
                    const borderColor = isWithinTarget ? THEME.success : THEME.error;

                    return (
                        <Box
                            key={stage.stageName}
                            sx={{
                                p: 2.5,
                                borderRadius: "10px",
                                backgroundColor: bgColor,
                                borderLeft: `4px solid ${borderColor}`,
                                transition: "transform 0.2s",
                                "&:hover": {
                                    transform: "translateY(-2px)"
                                }
                            }}
                        >
                            {/* Status Name */}
                            <Typography
                                sx={{
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    color: THEME.textPrimary,
                                    mb: 1.5
                                }}
                            >
                                {stage.stageName}
                            </Typography>

                            {/* Avg Days - Large Number */}
                            <Typography
                                sx={{
                                    fontSize: "32px",
                                    fontWeight: 700,
                                    color: textColor,
                                    lineHeight: 1
                                }}
                            >
                                {stage.avgDays}
                                <Typography
                                    component="span"
                                    sx={{
                                        fontSize: "14px",
                                        fontWeight: 500,
                                        color: textColor,
                                        ml: 0.5
                                    }}
                                >
                                    days
                                </Typography>
                            </Typography>

                            {/* Target */}
                            <Typography
                                sx={{
                                    fontSize: "12px",
                                    color: THEME.textSecondary,
                                    mt: 1
                                }}
                            >
                                Target: ≤{stage.targetDays} days
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    minHeight: 45,
                    borderTop: `2px solid ${THEME.border}`,
                    mt: 2,
                    pt: 1
                }}
            >
                <CTLExploreDataButton
                    query={generateSLAPerformanceQuery({ dateRange })}
                    chartTitle="SLA Performance"
                />
            </Box>
        </Paper>
    );
};

export default SLAPerformance;
